import { z } from 'zod';
import { CompliancePackStatusSchema, ComplianceRuleTypeSchema, ComplianceVerificationSchema, WorkTypeSchema, } from '../lib/enums';
import { zIsoDateTime, zJurisdiction, zStateCode, zUuid } from './common';
/**
 * Compliance reference data — informational tooling, NEVER legal advice.
 * Every rule carries a non-empty `boundaryNote`, and every surface rendering
 * rule content MUST render it (plus the pack's verification label). The
 * product never asserts that anything "is compliant".
 */
/** Where a disclosure block lands in the proposal document. */
export const PlacementSchema = z
    .object({
    position: z.enum(['cover', 'before_pricing', 'after_pricing', 'with_signature', 'footer']),
    order: z.number().int().min(0).default(0),
})
    .strict();
export const ComplianceRuleSchema = z.object({
    id: zUuid,
    packId: zUuid,
    ruleKey: z.string().min(1).max(120),
    ruleType: ComplianceRuleTypeSchema,
    workType: WorkTypeSchema,
    /** Trade profile keys the rule targets; empty = all trades. */
    tradeKeys: z.array(z.string().min(1).max(64)),
    title: z.string().min(1).max(300),
    bodyMd: z.string().min(1),
    /** Rule-type-specific parameters (e.g. deadline day counts). Data, not code. */
    params: z.record(z.unknown()),
    placement: PlacementSchema.nullable(),
    /** MANDATORY boundary language, rendered verbatim wherever the rule appears. */
    boundaryNote: z.string().min(1),
    /** Denormalized from the pack for display badges. */
    verification: ComplianceVerificationSchema,
    jurisdiction: zJurisdiction,
    packVersion: z.number().int().min(1),
});
export const CompliancePackSchema = z.object({
    id: zUuid,
    /** 'US' = federal anchor, otherwise a state code. */
    jurisdiction: zJurisdiction,
    version: z.number().int().min(1),
    status: CompliancePackStatusSchema,
    verification: ComplianceVerificationSchema,
    /** Citations for where content came from (labels + citations; local data only). */
    sourcesLog: z.array(z
        .object({
        label: z.string().min(1).max(300),
        citation: z.string().min(1).max(1000),
        accessedAt: zIsoDateTime.nullable().optional(),
    })
        .passthrough()),
    ruleCount: z.number().int().min(0),
    createdAt: zIsoDateTime,
});
/** GET /compliance/rules?state=&workType=&trade= */
export const ComplianceRulesQuerySchema = z.object({
    state: zStateCode.optional(),
    workType: WorkTypeSchema.optional(),
    trade: z.string().max(64).optional(),
    ruleType: ComplianceRuleTypeSchema.optional(),
});
export const ComplianceRulesResponseSchema = z.array(ComplianceRuleSchema);
export const CompliancePacksResponseSchema = z.array(CompliancePackSchema);
//# sourceMappingURL=compliance.js.map